import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Row, Col, Statistic, Typography, Divider, Empty, Table, Alert, Select, Spin, Tooltip,
} from 'antd';
import {
  SearchOutlined, LinkOutlined, WarningOutlined, ThunderboltOutlined,
  KeyOutlined, PieChartOutlined,
} from '@ant-design/icons';
import { Line, Column } from '@ant-design/plots';
import { ColumnsType } from 'antd/es/table';
import { ApiService } from '../services/api';

const { Text, Title } = Typography;

const CARD_STYLE: React.CSSProperties = { width: '100%' };
const SUB_STYLE: React.CSSProperties = {
  fontSize: 12,
  display: 'block',
  marginTop: 4,
  minHeight: 18,
};

// view 已把 urlpath 標成中文類型，這裡沿用同樣的字串，避免兩邊定義漂移
const TYPE_SEARCH = '廣告監測';
const TYPE_PARSE = '社群網址解析';
const ALL_ORGS = '__ALL_ORGS__';

interface UsageRow {
  usage_date: string;
  api_key: string;
  org_name: string;
  project_name: string;
  api_type: string;
  parse_type: string;
  volume_tier: string;
  quota_30d: number | null;
  api_calls: number;
  ok_calls: number;
  throttled_calls: number;
  forbidden_calls: number;
  notfound_calls: number;
  server_err_calls: number;
  request_units: number;
  expected_max_ads: number;
  ads_returned: number;
  keyword_queries: number;
  page_id_queries: number;
  ad_library_queries: number;
}

interface LatencyRow {
  org_name: string | null; // null = 全部客戶（SQL GROUPING SETS 的合計列）
  api_type: string;
  requests: number;
  error_requests: number;
  error_rate: number;
  throttled_requests: number;
  p50_latency: number;
  p95_latency: number;
  p99_latency: number;
  avg_latency: number;
  max_latency: number;
}

interface KeywordRow {
  keyword: string;
  usage_count: number;
  customer_count: number;
  active_days: number;
  last_used_date: string;
  orgs: string;
}

interface AdsApiAnalysisProps {
  data: {
    data?: Array<Record<string, any>>;
    time_range?: { start_date?: string; end_date?: string };
  } | null;
}

const sumBy = <T,>(rows: T[], pick: (r: T) => number): number =>
  rows.reduce((s, r) => s + (pick(r) || 0), 0);

const AdsApiAnalysis: React.FC<AdsApiAnalysisProps> = ({ data }) => {
  const rows: UsageRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as unknown as UsageRow[]) : []),
    [data]
  );

  const startDate = data?.time_range?.start_date;
  const endDate = data?.time_range?.end_date;

  const [selectedOrg, setSelectedOrg] = useState<string>(ALL_ORGS);
  const [keywords, setKeywords] = useState<KeywordRow[]>([]);
  const [latency, setLatency] = useState<LatencyRow[]>([]);
  const [kwLoading, setKwLoading] = useState(false);

  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setKwLoading(true);
    const params = { start_date: startDate, end_date: endDate };
    Promise.all([
      ApiService.getAdsKeywords(params).catch(() => null),
      ApiService.getAdsLatency(params).catch(() => null),
    ])
      .then(([kw, lat]) => {
        if (cancelled) return;
        setKeywords(Array.isArray(kw?.data) ? kw.data : []);
        setLatency(Array.isArray(lat?.data) ? lat.data : []);
      })
      .finally(() => {
        if (!cancelled) setKwLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const orgTotals = useMemo(() => {
    const acc: Record<string, number> = {};
    rows.forEach((r) => {
      acc[r.org_name] = (acc[r.org_name] || 0) + r.api_calls;
    });
    return Object.entries(acc)
      .map(([org_name, total]) => ({ org_name, total }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  const isAll = selectedOrg === ALL_ORGS;
  const scoped = useMemo(
    () => rows.filter((r) => isAll || r.org_name === selectedOrg),
    [rows, selectedOrg, isAll]
  );

  const searchRows = useMemo(() => scoped.filter((r) => r.api_type === TYPE_SEARCH), [scoped]);
  const parseRows = useMemo(() => scoped.filter((r) => r.api_type === TYPE_PARSE), [scoped]);

  // 逐日彙總；同一天可能有多列（不同客戶/解析類型），要先加總再畫
  const dailyOf = (src: UsageRow[], pick: (r: UsageRow) => number) => {
    const acc: Record<string, number> = {};
    src.forEach((r) => {
      acc[r.usage_date] = (acc[r.usage_date] || 0) + (pick(r) || 0);
    });
    return Object.keys(acc)
      .sort()
      .map((d) => ({ date: d, value: acc[d] }));
  };

  const searchDaily = useMemo(() => {
    const calls = dailyOf(searchRows, (r) => r.api_calls);
    const kws = dailyOf(searchRows, (r) => r.keyword_queries);
    return [
      ...calls.map((p) => ({ ...p, series: '呼叫次數' })),
      ...kws.map((p) => ({ ...p, series: '關鍵字查詢數' })),
    ];
  }, [searchRows]);

  const parseDaily = useMemo(
    () => dailyOf(parseRows, (r) => r.api_calls).map((p) => ({ ...p, series: '呼叫次數' })),
    [parseRows]
  );

  // /parse 的「渠道」＝ view 已分類好的 parse_type。
  // 括號開頭的幾種不是網址類型，而是「根本沒進到解析」的狀態（被限流、5xx、
  // 應用層沒記到）。混進長條圖會讓最高的那根看起來像某種連結來源，直接誤導判讀，
  // 因此排除；數量另外用文字交代，不是默默丟掉。
  const NON_URL_BUCKET = (t: string) => t.startsWith('(');

  const parseTypeAll = useMemo(() => {
    const acc: Record<string, number> = {};
    parseRows.forEach((r) => {
      acc[r.parse_type] = (acc[r.parse_type] || 0) + r.api_calls;
    });
    return Object.entries(acc)
      .map(([parse_type, calls]) => ({ parse_type, calls }))
      .sort((a, b) => b.calls - a.calls);
  }, [parseRows]);

  const parseTypes = useMemo(
    () => parseTypeAll.filter((t) => !NON_URL_BUCKET(t.parse_type)),
    [parseTypeAll]
  );

  const excludedBuckets = useMemo(
    () => parseTypeAll.filter((t) => NON_URL_BUCKET(t.parse_type)),
    [parseTypeAll]
  );

  const searchTotals = useMemo(
    () => ({
      calls: sumBy(searchRows, (r) => r.api_calls),
      ok: sumBy(searchRows, (r) => r.ok_calls),
      throttled: sumBy(searchRows, (r) => r.throttled_calls),
      keywordQueries: sumBy(searchRows, (r) => r.keyword_queries),
      pageIdQueries: sumBy(searchRows, (r) => r.page_id_queries),
      adLibQueries: sumBy(searchRows, (r) => r.ad_library_queries),
      adsReturned: sumBy(searchRows, (r) => r.ads_returned),
      expected: sumBy(searchRows, (r) => r.expected_max_ads),
    }),
    [searchRows]
  );

  const parseTotals = useMemo(
    () => ({
      calls: sumBy(parseRows, (r) => r.api_calls),
      ok: sumBy(parseRows, (r) => r.ok_calls),
      throttled: sumBy(parseRows, (r) => r.throttled_calls),
    }),
    [parseRows]
  );

  // 分位數不是可加的量，不能把各客戶的 p95 平均起來；後端已用 GROUPING SETS
  // 同時算好「單一客戶」與「全部客戶」兩層，這裡只挑對應的那一列。
  const latencyOf = (apiType: string): LatencyRow | undefined =>
    latency.find(
      (l) => l.api_type === apiType && (isAll ? l.org_name === null : l.org_name === selectedOrg)
    );

  const scopedKeywords = useMemo(
    () => (isAll ? keywords : keywords.filter((k) => (k.orgs || '').includes(selectedOrg))),
    [keywords, selectedOrg, isAll]
  );

  if (!rows.length) {
    return <Empty description="此區間沒有廣告監測 API 使用紀錄" style={{ padding: '60px 0' }} />;
  }

  const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);
  // 分母為 0 時直接給 0，否則畫面會出現 NaN 或 Infinity
  const ratio = (n: number, d: number) => (d > 0 ? n / d : 0);
  const avgKeywordsPerCall = ratio(searchTotals.keywordQueries, searchTotals.calls);
  const avgAdsPerCall = ratio(searchTotals.adsReturned, searchTotals.calls);
  const avgAdsPerKeyword = ratio(searchTotals.adsReturned, searchTotals.keywordQueries);
  const scopeLabel = isAll ? '全部客戶' : selectedOrg;


  // 秒數在不同量級要用不同精度：37.3s 與 0.892s 都要看得懂
  const fmtSec = (v?: number) => {
    if (v === undefined || v === null) return '—';
    if (v >= 100) return `${Math.round(v)}s`;
    if (v >= 10) return `${v.toFixed(1)}s`;
    return `${v.toFixed(2)}s`;
  };

  const renderLatency = (apiType: string) => {
    const l = latencyOf(apiType);
    if (kwLoading) {
      return (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }} aria-busy="true">
            <Spin />
          </div>
        </Card>
      );
    }
    if (!l) {
      return (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Empty description="此區間沒有可統計的回應時間" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      );
    }
    return (
      <Row gutter={16} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><WarningOutlined /> 錯誤率</span>}
              value={l.error_rate}
              precision={2}
              suffix="%"
              valueStyle={l.error_rate > 10 ? { color: '#cf1322' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>
              {l.error_requests.toLocaleString()} / {l.requests.toLocaleString()} 次
              {l.error_requests > 0 &&
                `（限流佔 ${((l.throttled_requests / l.error_requests) * 100).toFixed(0)}%）`}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title="回應時間 p50（中位數）" value={fmtSec(l.p50_latency)} />
            <Text type="secondary" style={SUB_STYLE}>平均 {fmtSec(l.avg_latency)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title="回應時間 p95"
              value={fmtSec(l.p95_latency)}
              valueStyle={l.p95_latency > 30 ? { color: '#d46b08' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>95% 的請求快於此</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title="回應時間 p99"
              value={fmtSec(l.p99_latency)}
              valueStyle={l.p99_latency > 60 ? { color: '#cf1322' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>最慢 {fmtSec(l.max_latency)}</Text>
          </Card>
        </Col>
      </Row>
    );
  };

  const lineCfg = (d: any[], colors: string[]): any => ({
    data: d,
    xField: 'date',
    yField: 'value',
    colorField: 'series',
    scale: { color: { range: colors } },
    axis: {
      y: { labelFormatter: (v: number) => Number(v).toLocaleString() },
      x: { labelAutoRotate: true },
    },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
  });

  const parseTypeCfg: any = {
    data: parseTypes.slice(0, 12),
    xField: 'parse_type',
    yField: 'calls',
    colorField: 'parse_type',
    legend: false,
    axis: {
      x: { labelAutoRotate: true, labelAutoHide: false },
      y: { labelFormatter: (v: number) => Number(v).toLocaleString() },
    },
  };

  const kwColumns: ColumnsType<KeywordRow> = [
    {
      title: '關鍵字',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (v: string) => <Text code copyable={{ text: v }}>{v}</Text>,
    },
    {
      title: '搜尋次數',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 110,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.usage_count - b.usage_count,
      render: (v: number) => v.toLocaleString(),
    },
    {
      title: '使用 API key 數',
      dataIndex: 'customer_count',
      key: 'customer_count',
      width: 130,
      sorter: (a, b) => a.customer_count - b.customer_count,
    },
    {
      title: '出現天數',
      dataIndex: 'active_days',
      key: 'active_days',
      width: 100,
      sorter: (a, b) => a.active_days - b.active_days,
    },
    {
      title: '最後使用',
      dataIndex: 'last_used_date',
      key: 'last_used_date',
      width: 120,
      render: (v: string) => (v ? String(v).slice(0, 10) : '—'),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Text type="secondary">
          統計區間：{startDate} ~ {endDate || '今天'}（已排除 QSearch 內部流量）
        </Text>
        <Select
          showSearch
          style={{ minWidth: 280 }}
          value={selectedOrg}
          onChange={setSelectedOrg}
          optionFilterProp="label"
          options={[
            { label: `全部客戶（${orgTotals.length} 家加總）`, value: ALL_ORGS },
            ...orgTotals.map((o) => ({
              label: `${o.org_name}（${o.total.toLocaleString()}）`,
              value: o.org_name,
            })),
          ]}
        />
      </div>
      <Divider />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="廣告監測分兩種用途，指標不同故分開呈現"
        description={
          <>
            <Text strong>廣告監測（/search）</Text>：真的去搜尋 Meta 廣告庫，因此看關鍵字與回傳廣告量。
            <br />
            <Text strong>社群網址解析（/parse）</Text>：只做網址轉譯，沒有關鍵字，因此看解析類型分佈。
            <br />
            <br />
            廣告監測一次搜尋可用三種方式指定要查什麼：
            <Text strong>關鍵字</Text>（如「贈書」，查提及該詞的廣告）、
            <Text strong>粉專 ID</Text>（查某個粉專投放的所有廣告）、
            <Text strong>廣告庫 ID</Text>（查 Meta 廣告檔案庫裡指定的某一則廣告）。
            實際上 <Text strong>99.9% 是關鍵字</Text>，另外兩種近 30 天合計不到 10 次，
            因此本頁的關鍵字排行只統計關鍵字那一種。
            <br />
            <br />
            資料源為 <Text code>v_ads_api_usage_daily</Text>，關鍵字與廣告回傳量來自應用層 log
            （HTTP log 沒有這些欄位）。
          </>
        }
      />

      {/* ── /search ───────────────────────────────────────────── */}
      <Title level={4}><SearchOutlined /> 廣告監測（/api/ads/v3/search）— {scopeLabel}</Title>
      <Row gutter={16} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title="呼叫次數" value={searchTotals.calls} />
            <Text type="secondary" style={SUB_STYLE}>成功率 {pct(searchTotals.ok, searchTotals.calls).toFixed(1)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><KeyOutlined /> 關鍵字查詢數</span>} value={searchTotals.keywordQueries} />
            <Tooltip title="同一支 API 的另外兩種查法：粉專 ID 查該粉專的所有廣告、廣告庫 ID 查指定的某一則廣告。用量極低，僅供對照。">
              <Text type="secondary" style={{ ...SUB_STYLE, cursor: 'help' }}>
                另有粉專 {searchTotals.pageIdQueries.toLocaleString()}、廣告庫{' '}
                {searchTotals.adLibQueries.toLocaleString()} 次
              </Text>
            </Tooltip>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title="平均每次搜尋關鍵字數"
              value={avgKeywordsPerCall}
              precision={1}
              suffix="個"
            />
            <Text type="secondary" style={SUB_STYLE}>單次呼叫可帶多個關鍵字</Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title="回傳廣告數" value={searchTotals.adsReturned} />
            <Text type="secondary" style={SUB_STYLE}>
              達預期上限 {pct(searchTotals.adsReturned, searchTotals.expected).toFixed(0)}%
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title="平均每次回傳廣告"
              value={avgAdsPerCall}
              precision={1}
              suffix="則"
            />
            <Text type="secondary" style={SUB_STYLE}>
              每個關鍵字平均 {avgAdsPerKeyword.toFixed(1)} 則
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={4} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><WarningOutlined /> 被限流</span>}
              value={searchTotals.throttled}
              valueStyle={searchTotals.throttled > 0 ? { color: '#d46b08' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>佔 {pct(searchTotals.throttled, searchTotals.calls).toFixed(1)}%</Text>
          </Card>
        </Col>
      </Row>

      {renderLatency(TYPE_SEARCH)}

      <Card title="每日趨勢：呼叫次數 vs 關鍵字查詢數" style={{ marginBottom: 16 }}>
        {searchDaily.length ? (
          <Line {...lineCfg(searchDaily, ['#1677ff', '#722ed1'])} height={320} />
        ) : (
          <Empty description="此區間沒有廣告監測呼叫" />
        )}
      </Card>

      <Card
        title={<span><KeyOutlined /> 最常被搜尋的關鍵字 — {scopeLabel}</span>}
        style={{ marginBottom: 32 }}
      >
        {kwLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }} aria-busy="true">
            <Spin size="large" />
          </div>
        ) : (
          <Table<KeywordRow>
            dataSource={scopedKeywords}
            columns={kwColumns}
            rowKey="keyword"
            size="small"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: '此區間沒有關鍵字搜尋紀錄' }}
          />
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          只計入 type=keyword 的查詢；查特定粉專 ID 或廣告庫 ID 不算關鍵字搜尋。
        </Text>
      </Card>

      {/* ── /parse ────────────────────────────────────────────── */}
      <Title level={4}><LinkOutlined /> 社群網址解析（/api/ads/v3/parse）— {scopeLabel}</Title>
      <Row gutter={16} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title="呼叫次數" value={parseTotals.calls} />
            <Text type="secondary" style={SUB_STYLE}>成功率 {pct(parseTotals.ok, parseTotals.calls).toFixed(1)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><WarningOutlined /> 被限流</span>}
              value={parseTotals.throttled}
              valueStyle={parseTotals.throttled > 0 ? { color: '#d46b08' } : undefined}
            />
            <Text type="secondary" style={SUB_STYLE}>佔 {pct(parseTotals.throttled, parseTotals.calls).toFixed(1)}%</Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic title={<span><PieChartOutlined /> 解析類型數</span>} value={parseTypes.length} />
            <Text type="secondary" style={SUB_STYLE}>
              最多：{parseTypes[0]?.parse_type ?? '—'}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6} style={{ display: 'flex' }}>
          <Card size="small" style={CARD_STYLE}>
            <Statistic
              title={<span><ThunderboltOutlined /> 使用客戶</span>}
              value={new Set(parseRows.map((r) => r.org_name)).size}
              suffix="家"
            />
            <Text type="secondary" style={SUB_STYLE}>此端點只做網址轉譯</Text>
          </Card>
        </Col>
      </Row>

      {renderLatency(TYPE_PARSE)}

      <Card title="每日趨勢：解析呼叫次數" style={{ marginBottom: 16 }}>
        {parseDaily.length ? (
          <Line {...lineCfg(parseDaily, ['#13c2c2'])} height={300} />
        ) : (
          <Empty description="此區間沒有解析呼叫" />
        )}
      </Card>

      <Card title={<span><PieChartOutlined /> 解析類型分佈（前 12 名）</span>} style={{ marginBottom: 16 }}>
        {parseTypes.length ? (
          <>
            <Column {...parseTypeCfg} height={320} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              「unknown: …」是解析器認不出的網址，依來源網站細分以便追查。
              {excludedBuckets.length > 0 && (
                <>
                  {' '}另有未進入解析的請求未列入本圖（
                  {excludedBuckets
                    .map((b) => `${b.parse_type} ${b.calls.toLocaleString()}`)
                    .join('、')}
                  ），那些是狀態而非網址類型。
                </>
              )}
            </Text>
          </>
        ) : (
          <Empty description="此區間沒有解析紀錄" />
        )}
      </Card>

    </>
  );
};

export default AdsApiAnalysis;
